import { useCallback, useEffect, useState } from "react"
import { useAuth } from "@/contexts/AuthContext"
import {
  getProgramMeta,
  getProgrammeIdFromWizard,
  getProgramProgress,
} from "@/lib/programs"
import { getProgramWeek, getProgramName } from "@/data/labels"

/**
 * État du bloc en cours, pour la carte « Mon bloc » du Profil et l'écran Mon programme.
 *
 * CORRECTION DU BUG DE PROGRESSION (lot 1)
 * ----------------------------------------
 * La carte affichait « Progression 100 % » et « 0 / 35 séances complétées » en même
 * temps : la barre était calculée sur les semaines écoulées depuis program_week_start,
 * le compteur sur les séances réalisées — et ce compteur était de surcroît codé en dur
 * à 0 dans useProfile. Deux vérités contradictoires sur la même carte.
 *
 * Décision produit appliquée ici : **une seule barre, calée sur les semaines du bloc**.
 * Le hook n'expose donc plus de pourcentage de progression du tout ; il expose la
 * semaine courante et le total de semaines, et l'UI dessine un segment par semaine.
 * Le compteur de séances reste exposé, mais comme une donnée distincte — et lue en base,
 * plus mockée. Deux nombres qui ne prétendent plus mesurer la même chose.
 *
 * `weeksTotal` et `sessionsTotal` viennent de la table `programs` (duration_weeks,
 * sessions_total) et non plus de constantes 12 / 35 : tous les programmes du catalogue
 * ne durent pas 12 semaines.
 */
export interface ProgramBlockState {
  programmeId: string | null
  programName: string
  /** Semaine en cours, bornée à weeksTotal. */
  weekCurrent: number
  weeksTotal: number
  sessionsCompleted: number
  sessionsTotal: number
  /** Date de début du bloc (wizard_submissions.program_week_start). */
  blockStartDate: string | null
  /** Dernière semaine du bloc : déclenche l'encart d'appel au test de force. */
  isFinalWeek: boolean
  /** Numéros de séance des tests 1RM du bloc, dans l'ordre : [initial, intermédiaire, final]. */
  testingSessions: number[]
  /** Numéro de la prochaine séance de test 1RM, ou null s'il n'en reste aucune. */
  nextTestSession: number | null
  /** Numéro du dernier test 1RM déjà franchi, ou null avant le premier. */
  lastTestSession: number | null
  loading: boolean
}

const FALLBACK_WEEKS = 12

export function useProgramBlock(): ProgramBlockState & { refresh: () => Promise<void> } {
  const { wizardData } = useAuth()

  const [meta, setMeta] = useState<{ sessions_total: number; duration_weeks: number; testing_sessions: number[] } | null>(null)
  const [progress, setProgress] = useState({ completedCount: 0, nextSessionNumber: 1 })
  const [loading, setLoading] = useState(true)

  const programmeId = wizardData ? getProgrammeIdFromWizard(wizardData) : null

  const load = useCallback(async () => {
    if (!wizardData || !programmeId) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [m, p] = await Promise.all([
        getProgramMeta(programmeId),
        getProgramProgress(wizardData.user_id, programmeId),
      ])
      setMeta(m)
      setProgress(p)
    } catch (err) {
      console.error("[useProgramBlock] chargement échoué :", err)
    } finally {
      setLoading(false)
    }
  }, [wizardData, programmeId])

  useEffect(() => { void load() }, [load])

  const weeksTotal = meta?.duration_weeks ?? FALLBACK_WEEKS
  const weekCurrent = getProgramWeek(wizardData?.program_week_start, weeksTotal)
  const sessionsTotal = meta?.sessions_total ?? 0

  // Les tests sont posés à des numéros de séance fixes par niveau (programs.testing_sessions).
  // Le curseur est `nextSessionNumber` — la première séance NON faite — et non le nombre de
  // séances terminées : un client qui a sauté la séance de test pour enchaîner sur la
  // suivante doit toujours voir ce test-là comme le prochain, pas celui d'après.
  const testingSessions = meta?.testing_sessions ?? []
  const nextTestSession = testingSessions.find((n) => n >= progress.nextSessionNumber) ?? null
  // Le dernier test franchi sert de référence de fraîcheur aux relevés de mesures :
  // un relevé est à jour s'il tombe dans la semaine de ce test-là.
  const lastTestSession = [...testingSessions].reverse().find((n) => n < progress.nextSessionNumber) ?? null

  return {
    programmeId,
    programName: wizardData ? getProgramName(wizardData.level_id, wizardData.goal_id) : "—",
    weekCurrent,
    weeksTotal,
    sessionsCompleted: progress.completedCount,
    sessionsTotal,
    blockStartDate: wizardData?.program_week_start ?? null,
    testingSessions,
    lastTestSession,
    isFinalWeek: weekCurrent >= weeksTotal,
    nextTestSession,
    loading,
    refresh: load,
  }
}
